import { useContext, useEffect, useMemo, useState } from "react";
import AuthClient from "../api/AuthClient";
import PromoterGroupsApi from "../api/PromoterGroupsApi";
import { parseMyPartyResponse } from "../api/parseResponse/parseMyPartiesResponse";
import { SelectedGroupContext } from "../GlobalContext/groupDetailContext";
import { PartyModel } from "../types";

export type SelectedGroupRequestState = "idle" | "loading" | "success" | "error";

const inFlightGroupRequests = new Map<string, Promise<PartyModel | null>>();

export const fetchPromoterSelectedGroup = async (groupId: string): Promise<PartyModel | null> => {
  const existingRequest = inFlightGroupRequests.get(groupId);

  if (existingRequest) {
    return existingRequest;
  }

  const request = (async () => {
    const api = new PromoterGroupsApi(new AuthClient());
    const response = await api.getPromoterGroup(groupId);

    if (!response?.data) {
      return null;
    }

    return parseMyPartyResponse(response.data);
  })();

  inFlightGroupRequests.set(groupId, request);

  try {
    return await request;
  } finally {
    inFlightGroupRequests.delete(groupId);
  }
};

export const usePromoterSelectedGroup = (groupId?: string) => {
  const { selectedGroup, setSelectedGroup } = useContext(SelectedGroupContext);
  const [requestState, setRequestState] = useState<SelectedGroupRequestState>("idle");

  useEffect(() => {
    if (!groupId) {
      setRequestState("error");
      return;
    }

    if (selectedGroup?.party.id === groupId) {
      setRequestState("success");
      return;
    }

    let isCancelled = false;

    const loadGroup = async () => {
      setRequestState("loading");

      try {
        const parsedGroup = await fetchPromoterSelectedGroup(groupId);

        if (isCancelled) {
          return;
        }

        if (parsedGroup) {
          setSelectedGroup(parsedGroup);
          setRequestState("success");
          return;
        }

        setRequestState("error");
      } catch (error) {
        if (!isCancelled) {
          console.error("Error fetching promoter group:", error);
          setRequestState("error");
        }
      }
    };

    void loadGroup();

    return () => {
      isCancelled = true;
    };
  }, [groupId, selectedGroup?.party.id, setSelectedGroup]);

  return useMemo(() => ({
    selectedGroup: selectedGroup?.party.id === groupId ? selectedGroup : null,
    requestState,
  }), [groupId, requestState, selectedGroup]);
};
